import React, { useState } from "react";
import { Layout, Card, Flex, Typography } from "antd";
import Sidebar from "../../general/components/left-menu/Sidebar";
import TopNavbar from "../../general/components/top-bar/TopNavbar";
import QuickOverview from "../../general/components/quick-overview/QuickOverview";
import UserOverview from "./UserOverview";
import LeadsTemplate from "../../general/components/leads-template/LeadsTemplate";
import QuickAccessWorkspace from "../../general/components/quick-access/QuickAccessWorkspace";
import PipelineWorkspace from "../../general/components/pipeline/PipelineWorkspace";
import ActivitiesCalendar from "../../general/components/activities/ActivitiesCalendar";
import ClientOnboardRequestsSection from "../../general/components/startup_doctor/ClientOnboardRequestsSection";
import BottomNavigation from "../../general/components/bottom-navigation/BottomNavigation";

import { useSelector } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const LEAD_WORKSPACE_TABS = new Set(["leads", "interested", "dnp", "converted", "favourite", "today-calls", "our_clients", "crm_clients", "clients", "remarks_filter"]);

const QUICK_ACCESS_TABS = new Set(["invoice", "invoices", "quotation", "quotations", "proposals", "proposal"]);

const getTabTitle = (tab) => {
  const titles = {
    overview: "My Workspace",
    leads: "My Assigned Leads",
    interested: "Hot / Qualified Leads",
    dnp: "Unreachable / Callbacks",
    converted: "Converted",
    favourite: "Priority Leads",
    "today-calls": "Today’s Call Log",
    activities: "Schedule",
    pipeline: "Pipeline",
    crm_client_onboard_requests: "Onboarding",
    invoice: "Invoices",
    invoices: "Invoices",
    quotation: "Quotations",
    quotations: "Quotations",
    proposals: "Proposals",
    proposal: "Proposals",
    support: "Help",
  };
  return titles[tab] || tab.replaceAll("_", " ");
};

const resolveEmployeeId = (user) => {
  return user?.id || user?._id || user?.employeeId || getCookie("userId");
};

const isQuickAccessOrPipeline = (tab) => QUICK_ACCESS_TABS.has(tab) || tab === "pipeline";

const extractSearchTerm = (recordData) => {
  if (!recordData) return "";
  return recordData.data?.clientCompanyName || recordData.data?.leadCompanyName || recordData.data?.invoiceNumber || recordData.title || "";
};

const extractDealId = (recordData) => {
  if (!recordData) return null;
  return recordData.data?._id || recordData.data?.id || null;
};

const resolveQuickAccessFilter = (tab, recordData) => {
  if (isQuickAccessOrPipeline(tab) && recordData) {
    return {
      search: extractSearchTerm(recordData),
      dealId: extractDealId(recordData),
    };
  }
  return { search: "", dealId: null };
};

function DefaultPlaceholderCard({ activeTab }) {
  return (
    <Card bordered={false} style={{ borderRadius: 14, minHeight: 400 }}>
      <Flex vertical align="center" justify="center" style={{ padding: "4rem 0" }}>
        <Title level={3} style={{ margin: 0, textTransform: "capitalize" }}>
          {activeTab.replaceAll("_", " ")}
        </Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          This section is ready for your {activeTab} data and components.
        </Paragraph>
      </Flex>
    </Card>
  );
}

function UserDashboardContent({
  activeTab,
  employeeId,
  globalSearchTerm,
  quickAccessFilter,
  activitySubTab,
  onActivitySubTabChange,
  activityModalTrigger,
  pipelineFilters,
  setPipelineFilters,
  pipelineAddTrigger,
  onNavigateTab,
  onReady,
}) {
  if (activeTab === "overview") {
    return <UserOverview onNavigateTab={onNavigateTab} onReady={onReady} />;
  }
  if (LEAD_WORKSPACE_TABS.has(activeTab)) {
    return <LeadsTemplate activeTab={activeTab} role="employee" employeeId={employeeId} searchQuery={globalSearchTerm} />;
  }
  if (QUICK_ACCESS_TABS.has(activeTab)) {
    return <QuickAccessWorkspace activeTab={activeTab} role="employee" initialSearch={quickAccessFilter.search} />;
  }
  if (activeTab === "pipeline") {
    return (
      <PipelineWorkspace
        role="employee"
        employeeId={employeeId}
        initialSearch={quickAccessFilter.search}
        targetDealId={quickAccessFilter.dealId}
        pipelineFilters={pipelineFilters}
        onPipelineFiltersChange={setPipelineFilters}
        addDealTrigger={pipelineAddTrigger}
        hideHeaderControls={true}
      />
    );
  }
  if (activeTab === "activities") {
    return <ActivitiesCalendar role="employee" employeeId={employeeId} subTab={activitySubTab} onSubTabChange={onActivitySubTabChange} modalTrigger={activityModalTrigger} hideHeaderRail />;
  }
  if (activeTab === "crm_client_onboard_requests") {
    return <ClientOnboardRequestsSection role="employee" />;
  }
  return <DefaultPlaceholderCard activeTab={activeTab} />;
}

function UserDashboard({ onLogout, onReady }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [activitySubTab, setActivitySubTab] = useState("calendar");
  const [activityModalTrigger, setActivityModalTrigger] = useState(0);
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [quickAccessFilter, setQuickAccessFilter] = useState({ search: "", dealId: null });
  const [pipelineFilters, setPipelineFilters] = useState({ search: "", owner: "all", priority: "all" });
  const [pipelineAddTrigger, setPipelineAddTrigger] = useState(0);

  const user = useSelector((state) => state.session?.settings?.userInfo);
  const employeeId = resolveEmployeeId(user);

  const handleNavigateTab = (tab, recordData) => {
    setActiveTab(tab);
    setQuickAccessFilter(resolveQuickAccessFilter(tab, recordData));
  };

  const handleCreateActivity = () => {
    setActivityModalTrigger((prev) => prev + 1);
  };

  const effectiveCompanyCode = user?.companyCode || getCookie("companyCode") || "";

  return (
    <Layout hasSider style={{ height: "100vh", overflow: "hidden" }}>
      <Sidebar role="employee" activeTab={activeTab} onTabChange={(tabKey) => setActiveTab(tabKey)} />

      <Layout style={{ height: "100vh", overflow: "hidden", background: "#f8fafc", display: "flex", flexDirection: "column", transition: "all 0.2s ease" }}>
        <TopNavbar title={getTabTitle(activeTab)} activeTab={activeTab} onNavigateTab={handleNavigateTab} onLogout={onLogout} onSearch={setGlobalSearchTerm} />

        {/* Full-width Quick Overview Strip */}
        <QuickOverview
          role="employee"
          employeeId={employeeId}
          activeTab={activeTab}
          onNavigateTab={(tab) => setActiveTab(tab)}
          activitySubTab={activitySubTab}
          onActivitySubTabChange={setActivitySubTab}
          onCreateActivity={handleCreateActivity}
          pipelineFilters={pipelineFilters}
          onPipelineFiltersChange={setPipelineFilters}
          onAddDeal={() => setPipelineAddTrigger((prev) => prev + 1)}
        />

        {/* Content Area */}
        <Content
          className="hide-scrollbar"
          style={{
            padding: "1rem 1.25rem",
            flex: 1,
            overflowY: "auto",
          }}
        >
          <UserDashboardContent
            activeTab={activeTab}
            employeeId={employeeId}
            globalSearchTerm={globalSearchTerm}
            quickAccessFilter={quickAccessFilter}
            activitySubTab={activitySubTab}
            onActivitySubTabChange={setActivitySubTab}
            activityModalTrigger={activityModalTrigger}
            pipelineFilters={pipelineFilters}
            setPipelineFilters={setPipelineFilters}
            pipelineAddTrigger={pipelineAddTrigger}
            onNavigateTab={(tab) => setActiveTab(tab)}
            onReady={onReady}
          />
        </Content>

        <BottomNavigation
          role="employee"
          companyCode={effectiveCompanyCode}
          employeeId={employeeId}
          onNavigateTab={(tab) => setActiveTab(tab)}
        />
      </Layout>
    </Layout>
  );
}

export default UserDashboard;
